// A small "more" menu (6.3): a quiet round button (three dots) that opens a short list under it. The activity's
// Delete lives here, with a confirm step, so the action row is Remind, Edit and this; the space header has "Mark all as
// seen". Closes on Escape (the focus goes back to the button), a click outside, or choosing an item.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export function MoreMenu({ label, children, className = '' }: { label: string; className?: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const id = useId();
  const close = () => { setOpen(false); btn.current?.focus({ preventScroll: true }); };
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    // Escape with the focus outside the menu too (an item that was just replaced, as Cancel in a confirm).
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); setOpen(false); btn.current?.focus({ preventScroll: true }); } };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onEsc);
    // The first item takes the focus, so the keyboard can go on from there.
    const t = window.setTimeout(() => box.current?.querySelector<HTMLElement>('[role="menu"] button:not(:disabled)')?.focus({ preventScroll: true }), 0);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onEsc); window.clearTimeout(t); };
  }, [open]);
  // An item that had the focus went away (the menu's content changed): the first item takes it, so the keyboard stays in.
  useEffect(() => {
    if (open && (!document.activeElement || document.activeElement === document.body)) box.current?.querySelector<HTMLElement>('[role="menu"] button:not(:disabled)')?.focus({ preventScroll: true });
  });
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(box.current?.querySelectorAll<HTMLElement>('[role="menu"] button:not(:disabled)') ?? [])];
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
  };
  return (
    <div className={`more ${className}`} ref={box} onKeyDown={onKey}>
      <button ref={btn} type="button" className="ib morebtn" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
        onClick={() => setOpen(o => !o)}><IconMore /></button>
      {open && <div className="morepop" role="menu" id={id} aria-label={label}>{children(close)}</div>}
    </div>
  );
}

const IconMore = () => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5.5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="18.5" cy="12" r="1.7" /></svg>;
